import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Card, CardContent, Chip, Grid, LinearProgress,
  Stack, Tab, Tabs, Typography, useTheme,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { StatCard, SectionCard, EmptyState, StatusChip } from "../ui";
import { AutoAbsentChip, isAutoAbsentLeave, leaveTypeLabel, MonthlyLeaveSummaryCards } from "../Leave/leaveAllocationUi";
import { useAuth } from "../../context/AuthContext";
import { useModules } from "../../context/ModulesContext";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";

const BASE_QUICK_ACTIONS = [
  { id: "attendance", color: "primary", path: "/AttendancePortal" },
  { label: "Apply Leave", icon: <BeachAccessOutlinedIcon />, color: "pink", path: "/ApplyForLeave" },
  { label: "Claim Expense", icon: <PaymentsOutlinedIcon />, color: "success", path: "/ReimbursementForm", moduleKey: "reimbursement" },
  { label: "My Payslip", icon: <PaymentsOutlinedIcon />, color: "info", path: "/payroll/my-payslips" },
  { label: "Directory", icon: <PeopleAltOutlinedIcon />, color: "warning", path: "/directory" },
  { label: "My Goals", icon: <FlagOutlinedIcon />, color: "cyan", path: "/goals", moduleKey: "goals" },
];

const QA_COLORS = {
  primary: { bg: "#EDE8FA", text: "#6C50BF" },
  pink:    { bg: "#F4EDFA", text: "#8a3fb5" },
  success: { bg: "#EDFAF2", text: "#1a7f27" },
  info:    { bg: "#EDF3FA", text: "#1e4ea8" },
  warning: { bg: "#FFF4D6", text: "#8a6600" },
  cyan:    { bg: "#E6F6F4", text: "#2fa294" },
};

export default function DashboardNew() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isEnabled } = useModules();
  const isDark = theme.palette.mode === "dark";

  const [profile, setProfile] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [] });
  const [holidays, setHolidays] = useState([]);
  const [attendance, setAttendance] = useState({ present: 0, absent: 0, lopDays: 0, workingDays: 0 });
  const [pulse, setPulse] = useState(null); // company-wide "who's in today"
  const [pendingFbpCount, setPendingFbpCount] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [tab, setTab] = useState(0);
  // Today's attendance punch state: "none" | "in" (checked in, running) | "done"
  const [punchState, setPunchState] = useState("none");
  const [time, setTime] = useState(new Date());
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  // SR 224: dynamically fetched leave type label map so new leave types appear on the dashboard
  const [leaveTypeLabelMap, setLeaveTypeLabelMap] = useState({ annualLeave: "Annual", sickLeave: "Sick", casualleave: "Casual", Other: "Other" });
  const [monthlyLeave, setMonthlyLeave] = useState(null);
  const [lopDaysTaken, setLopDaysTaken] = useState(0);
  const [lopYearToDate, setLopYearToDate] = useState(0);
  const [compOffAvailable, setCompOffAvailable] = useState(0);

  const isAdminOrHr = ["admin", "hr"].includes((user?.role || user?.employee?.role || "").toLowerCase());

  useEffect(() => {
    if (location.pathname !== "/EmployeeDashboard") return undefined;

    const fetchTodayAttendance = async () => {
      try {
        const res = await api.get(endpoints.attendance.records);
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayRecord = list.find((r) => {
          const d = new Date(r.checkInTime || r.createdAt);
          return d >= today;
        });
        setIsCheckedIn(!!(todayRecord?.checkInTime && !todayRecord?.checkOutTime));
      } catch {
        setIsCheckedIn(false);
      }
    };
    fetchTodayAttendance();
    const onFocus = () => fetchTodayAttendance();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [location.pathname]);

  const quickActions = useMemo(
    () =>
      BASE_QUICK_ACTIONS.filter((a) => !a.moduleKey || isEnabled(a.moduleKey)).map((action) => {
        if (action.id !== "attendance") return action;
        return {
          ...action,
          label: isCheckedIn ? "Check Out" : "Check In",
          icon: isCheckedIn ? <LogoutOutlinedIcon /> : <LoginOutlinedIcon />,
          color: isCheckedIn ? "success" : "primary",
        };
      }),
    [isCheckedIn, isEnabled]
  );

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [me, leaves, ann, cel, hols, recs, pulseRes, remLeaves] = await Promise.allSettled([
          api.get(endpoints.selfService.me).catch(() => ({ data: { data: null } })),
          api.get(endpoints.leave.getLeaveRequest).catch(() => ({ data: [] })),
          (isEnabled("announcements")
            ? api.get(endpoints.announcements.list)
            : Promise.resolve({ data: { data: [] } })
          ).catch(() => ({ data: { data: [] } })),
          api.get(endpoints.selfService.calendarEvents).catch(() => ({ data: { data: { birthdays: [], anniversaries: [] } } })),
          api.get(endpoints.holidays.list).catch(() => ({ data: [] })),
          api.get(endpoints.attendance.records).catch(() => ({ data: [] })),
          api.get("/api/attendance/today-pulse").catch(() => ({ data: null })),
          api.get(endpoints.employees.remainingLeaves).catch(() => ({ data: null })),
        ]);
        setPulse(pulseRes.value?.data || null);
        // The "Check In" quick action was static, so after checking in the dashboard
        // still invited the user to check in and offered no way out. Derive today's
        // punch state the same way AttendancePortalNew does.
        const recList = Array.isArray(recs.value?.data) ? recs.value.data : recs.value?.data?.data || [];
        const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
        const todayRec = recList.find((r) => new Date(r.checkInTime || r.createdAt) >= dayStart);
        setPunchState(!todayRec ? "none" : todayRec.checkOutTime ? "done" : "in");
        setProfile(me.value?.data?.data || null);
        setMyLeaves(Array.isArray(leaves.value?.data) ? leaves.value.data : leaves.value?.data?.data || []);
        const rem = remLeaves.value?.data;
        setMonthlyLeave(rem?.monthlyLeave || me.value?.data?.monthlyLeave || null);
        setLopDaysTaken(Number(rem?.lopDaysTaken) || 0);
        setLopYearToDate(Number(rem?.lopYearToDate) || 0);
        setCompOffAvailable(Number(rem?.compOffAvailable) || 0);
        setAnnouncements(ann.value?.data?.data || []);
        setCelebrations(cel.value?.data?.data || { birthdays: [], anniversaries: [] });
        const holData = Array.isArray(hols.value?.data) ? hols.value.data : hols.value?.data?.data || [];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const upcoming = holData
          .filter((h) => new Date(h.date) >= today)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 5);
        setHolidays(upcoming);
      } catch (_) {}
    })();
  }, []);

  // SR 224: fetch leave types so new leave types appear with proper labels on dashboard
  useEffect(() => {
    api.get(endpoints.leave.types).then((res) => {
      const types = res.data?.data || res.data?.leaveTypes || res.data || [];
      if (Array.isArray(types) && types.length > 0) {
        const dynamicMap = { annualLeave: "Annual", sickLeave: "Sick", casualleave: "Casual", Other: "Other" };
        types.forEach((t) => {
          if (t.code) dynamicMap[t.code] = t.name || t.code;
          if (t.name) dynamicMap[t.name] = t.name;
        });
        setLeaveTypeLabelMap(dynamicMap);
      }
    }).catch(() => {}); // fallback to defaults on error
  }, []);

  useEffect(() => {
    if (!isAdminOrHr) return;
    api.get(`${endpoints.payroll.fbpAll}?status=submitted`)
      .then((res) => {
        const rows = res?.data?.data;
        setPendingFbpCount(Array.isArray(rows) ? rows.length : 0);
      })
      .catch(() => setPendingFbpCount(0));
  }, [isAdminOrHr]);

  const pendingLeaves = myLeaves.filter((l) => l.status === "pending").length;
  const yearNow = new Date().getFullYear();
  const paidDaysOf = (l) => {
    if (l?.paidDays != null && l.paidDays !== "") return Number(l.paidDays) || 0;
    return Number(l?.totalDays) || 0;
  };
  const approvedLeavesUsed = myLeaves
    .filter((l) => l.status === "approved" && new Date(l.startDate).getFullYear() === yearNow)
    .reduce((s, l) => s + paidDaysOf(l), 0);

  const remainingLeaves = useMemo(() => {
    if (!profile?.remainingLeave) return [];
    // SR 224: use dynamically fetched label map so newly added leave types show with correct labels
    return Object.entries(profile.remainingLeave).map(([k, v]) => ({ key: k, label: leaveTypeLabelMap[k] || k, value: v }));
  }, [profile, leaveTypeLabelMap]);

  const isSameCalendarDay = (dateLike, vs = new Date()) => {
    if (!dateLike) return false;
    const d = new Date(dateLike);
    return d.getMonth() === vs.getMonth() && d.getDate() === vs.getDate();
  };

  const todaysCelebrations = useMemo(() => {
    const now = new Date();
    const bdays = (celebrations.birthdays || [])
      .filter((p) => isSameCalendarDay(p.occursOn, now))
      .map((p) => ({ ...p, kind: "birthday" }));
    const years = (celebrations.anniversaries || [])
      .filter((p) => isSameCalendarDay(p.occursOn, now))
      .map((p) => ({ ...p, kind: "anniversary" }));
    return [...bdays, ...years];
  }, [celebrations]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Hero banner — Thunder AI gradient */}
      <Card
        sx={{
          mb: { xs: 2, md: 3 },
          background: "linear-gradient(135deg, #6C50BF 0%, #8B6BD1 50%, #492ba3 100%)",
          color: "white",
          border: "none",
          borderRadius: 4,
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-40%",
            right: "-10%",
            width: 400,
            height: 400,
            background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-30%",
            left: "-5%",
            width: 300,
            height: 300,
            background: "radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)",
            borderRadius: "50%",
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, md: 4 }, "&:last-child": { pb: { xs: 2.25, md: 4 } }, position: "relative", zIndex: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={{ xs: 1.75, md: 3 }}>
            <Stack direction="row" spacing={{ xs: 1.75, md: 2.5 }} alignItems="center">
              <Avatar
                src={profile?.profilePicture}
                sx={{
                  width: { xs: 52, md: 72 },
                  height: { xs: 52, md: 72 },
                  bgcolor: "rgba(255,255,255,0.2)",
                  fontSize: { xs: 20, md: 28 },
                  fontWeight: 700,
                  border: "3px solid rgba(255,255,255,0.3)",
                }}
              >
                {(profile?.name || user?.name || "U").slice(0, 1).toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: { xs: 13, md: 15 }, opacity: 0.9, fontWeight: 500, mb: 0.25 }}>
                  {greeting}, 👋
                </Typography>
                <Typography sx={{ fontSize: { xs: "1.35rem", md: "2rem" }, fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}>
                  {profile?.name || user?.name || "there"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {profile?.designation && <Chip size="small" label={profile.designation} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: 11, fontWeight: 600 }} />}
                  {profile?.department && <Chip size="small" label={profile.department} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontSize: 11 }} />}
                  {profile?.jobLocation && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <LocationOnOutlinedIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                      <Typography sx={{ fontSize: 11, opacity: 0.85 }}>{profile.jobLocation}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Stack spacing={1.5} direction={{ xs: "row", md: "column" }} alignItems={{ xs: "center", md: "flex-end" }}>
              <Chip
                icon={<AccessTimeOutlinedIcon sx={{ color: "white !important", fontSize: 14 }} />}
                label={time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600, height: 30 }}
              />
              <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                {time.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Today's celebrations — tight single-line strip */}
      {todaysCelebrations.length > 0 && (
        <Box
          sx={{
            mb: { xs: 1.25, md: 1.5 },
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            maxWidth: "100%",
            px: 1,
            py: 0.5,
            borderRadius: 999,
            border: "1px solid",
            borderColor: isDark ? "rgba(236,72,153,0.35)" : "rgba(236,72,153,0.25)",
            background: isDark
              ? "rgba(236,72,153,0.12)"
              : "linear-gradient(90deg, #FDF2F8, #ECFDF5)",
            overflowX: "auto",
            whiteSpace: "nowrap",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, pr: 0.5 }}>
            <CelebrationOutlinedIcon sx={{ fontSize: 16, color: "#BE185D" }} />
            <Typography sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1, color: "#BE185D", letterSpacing: 0.2 }}>
              Today
            </Typography>
          </Stack>
          {todaysCelebrations.map((p, i) => {
            const isBday = p.kind === "birthday";
            return (
              <React.Fragment key={`${p.kind}-${p.employeeid}`}>
                {i > 0 && (
                  <Box component="span" sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "text.disabled", flexShrink: 0, opacity: 0.5 }} />
                )}
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    flexShrink: 0,
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 999,
                    bgcolor: isBday
                      ? isDark ? "rgba(236,72,153,0.22)" : "#FCE7F3"
                      : isDark ? "rgba(16,185,129,0.22)" : "#D1FAE5",
                  }}
                >
                  <Avatar
                    src={p.profilePicture}
                    sx={{
                      width: 20,
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: isBday ? "#F9A8D4" : "#6EE7B7",
                      color: isBday ? "#9D174D" : "#065F46",
                    }}
                  >
                    {(p.name || "U").slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 800,
                      lineHeight: 1.2,
                      color: isBday ? "#9D174D" : "#065F46",
                    }}
                  >
                    {p.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: isBday ? "#BE185D" : "#047857",
                    }}
                  >
                    {isBday ? "· Birthday" : `· ${p.years || ""}y Anniversary`}
                  </Typography>
                </Stack>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {/* Stat cards */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} mb={{ xs: 2, md: 3 }}>
        <Grid item xs={6} md={4}>
          <StatCard
            label="Pending Leaves"
            value={pendingLeaves}
            icon={<BeachAccessOutlinedIcon />}
            color="warning"
            helperText={`${approvedLeavesUsed} approved YTD`}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <StatCard
            label="Leave Remaining"
            value={monthlyLeave?.remaining ?? remainingLeaves.find((r) => r.key === "annualLeave")?.value ?? 0}
            icon={<BeachAccessOutlinedIcon />}
            color="primary"
            helperText={monthlyLeave ? `${monthlyLeave.taken} used of ${monthlyLeave.accrued} accrued` : "days left"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {isAdminOrHr ? (
            <StatCard
              label="Pending FBP Submissions"
              value={pendingFbpCount === null ? "…" : pendingFbpCount}
              icon={<ReceiptLongOutlinedIcon />}
              color="info"
              helperText="Click to review"
              onClick={() => navigate("/payroll/fbp-admin")}
            />
          ) : (
            <StatCard
              label="Goals On Track"
              value="—"
              icon={<FlagOutlinedIcon />}
              color="info"
              helperText="Review upcoming"
              onClick={() => navigate("/goals")}
            />
          )}
        </Grid>
      </Grid>

      {/* Quick actions */}
      <SectionCard title="Quick Actions" subtitle="Jump straight to common tasks">
        <Grid container spacing={2}>
          {quickActions.map((q) => {
            const scheme = QA_COLORS[q.color];
            return (
              <Grid item xs={6} sm={4} md={2} key={q.id || q.label}>
                <Box
                  onClick={() => navigate(q.path)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    p: 2,
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: scheme.bg,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      bgcolor: scheme.bg,
                      color: scheme.text,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(q.icon, { sx: { fontSize: 22, color: scheme.text } })}
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.primary", textAlign: "center", lineHeight: 1.3 }}>
                    {q.label}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </SectionCard>

      {/* Today at InfyU — company-wide who's in */}
      {pulse && (
        <Box sx={{ mt: 2.5 }}>
          <SectionCard title="Today at InfyU" subtitle={new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}>
            {pulse.dayType === "holiday" ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>🎉 {pulse.holidayName || "Holiday"}</Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Office closed today · {pulse.totals.present} still checked in</Typography>
              </Box>
            ) : pulse.dayType === "weekly_off" ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>🌴 Weekly off</Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Not a working day · {pulse.totals.present} checked in</Typography>
              </Box>
            ) : (
              <>
                <Stack direction="row" alignItems="baseline" spacing={1} mb={1} flexWrap="wrap">
                  <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#6C50BF", lineHeight: 1 }}>
                    {pulse.totals.present}<Box component="span" sx={{ fontSize: 16, color: "#8b8fa3", fontWeight: 600 }}>/{pulse.totals.total}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>present today · {pulse.totals.percent}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pulse.totals.percent} sx={{ height: 8, borderRadius: 4, mb: 1.5, "& .MuiLinearProgress-bar": { bgcolor: "#39C645" } }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                  <Chip size="small" label={`✅ Present ${pulse.totals.present}`} sx={{ bgcolor: "#EDFAF2", color: "#065F46", fontWeight: 700 }} />
                  {pulse.totals.onLeave > 0 && <Chip size="small" label={`🌴 On leave ${pulse.totals.onLeave}`} sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />}
                  <Chip size="small" label={`⛔ Absent ${pulse.totals.absent}`} sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }} />
                </Stack>
                <Grid container spacing={2}>
                  {[["Present", pulse.present, "#065F46"], ["On leave", pulse.onLeave, "#92400E"], ["Absent", pulse.absent, "#991B1B"]].map(([label, list, color]) => (
                    <Grid item xs={12} sm={4} key={label}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color, mb: 0.75 }}>{label} · {list.length}</Typography>
                      <Stack spacing={0.5} sx={{ maxHeight: 210, overflowY: "auto", pr: 0.5 }}>
                        {list.length === 0 && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>—</Typography>}
                        {list.map((p) => (
                          <Stack key={p.employeeid} direction="row" alignItems="center" spacing={1}>
                            <Avatar src={p.profilePicture} sx={{ width: 24, height: 24, fontSize: 11 }}>{(p.name || "?")[0]}</Avatar>
                            <Typography sx={{ fontSize: 12.5, minWidth: 0 }} noWrap>{p.name}{p.remote ? " · 🏠" : ""}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </SectionCard>
        </Box>
      )}

      <Grid container spacing={2.5} mt={0.5}>
        {/* Leave Balance */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="My Leave Balance"
            subtitle={monthlyLeave ? `1.5 days/month · ${monthlyLeave.taken} used of ${monthlyLeave.accrued} accrued (cap ${monthlyLeave.yearCap})` : "Days remaining from the 1.5/month leave pool"}
            actions={[
              <Button key="apply" variant="contained" size="small" onClick={() => navigate("/ApplyForLeave")}>
                Apply Leave
              </Button>,
            ]}
          >
            {monthlyLeave ? (
              <MonthlyLeaveSummaryCards
                monthlyLeave={monthlyLeave}
                lopDaysTaken={lopDaysTaken}
                lopYearToDate={lopYearToDate}
                compOffAvailable={compOffAvailable}
                hideIntro
              />
            ) : remainingLeaves.length === 0 ? (
              <EmptyState title="No leave data" compact />
            ) : (
              <Stack spacing={2}>
                {remainingLeaves.filter((r) => r.key === "annualLeave").map((r) => {
                  const total = 18;
                  const pct = total ? Math.min(100, (r.value / total) * 100) : 0;
                  return (
                    <Box key={r.key}>
                      <Stack direction="row" justifyContent="space-between" mb={0.75}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Monthly leave</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>
                          {r.value} <Typography component="span" sx={{ fontSize: 11, color: "text.secondary", fontWeight: 500 }}>/ {total}</Typography>
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(108,80,191,0.1)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background: pct < 25
                              ? "linear-gradient(90deg, #ff1f35, #ff784a)"
                              : "linear-gradient(90deg, #6C50BF, #8B6BD1)",
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Announcements — hidden entirely when the module is toggled off */}
        {isEnabled("announcements") && (
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Announcements"
            subtitle={`${announcements.length} active`}
            actions={[
              <Button key="all" size="small" onClick={() => navigate("/announcements")}>
                View all
              </Button>,
            ]}
          >
            {announcements.length === 0 ? (
              <EmptyState title="No announcements" description="You're all caught up" compact icon={<NotificationsNoneOutlinedIcon sx={{ fontSize: 36 }} />} />
            ) : (
              <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: "auto" }}>
                {announcements.slice(0, 5).map((a) => (
                  <Box
                    key={a._id}
                    sx={{
                      p: 1.5,
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderLeftWidth: 4,
                      borderLeftColor: a.priority === "urgent" ? "error.main" : a.priority === "high" ? "warning.main" : "primary.main",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }} noWrap>
                          {a.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {a.body}
                        </Typography>
                      </Box>
                      <Chip size="small" label={a.category || "general"} variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>
        )}

        {/* My Leaves recent */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="My Recent Leaves"
            subtitle={`${myLeaves.length} total requests`}
            actions={[
              <Button key="all" size="small" onClick={() => navigate("/leave/my")}>
                View all
              </Button>,
            ]}
          >
            {myLeaves.length === 0 ? (
              <EmptyState title="No leave requests yet" description="Apply for leave when you need time off" compact />
            ) : (
              <Stack spacing={1.5}>
                {myLeaves.slice(0, 5).map((l) => (
                  <Stack
                    key={l._id}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ width: 4, alignSelf: "stretch", bgcolor: l.status === "approved" ? "success.main" : l.status === "rejected" ? "error.main" : "warning.main", borderRadius: 1 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {leaveTypeLabel(l.leaveType)} · {l.totalDays || 1} day{l.totalDays > 1 ? "s" : ""}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          {new Date(l.startDate).toLocaleDateString("en-GB")} → {new Date(l.endDate).toLocaleDateString("en-GB")}
                        </Typography>
                        {isAutoAbsentLeave(l) ? <AutoAbsentChip /> : null}
                      </Stack>
                    </Box>
                    <StatusChip status={l.status} />
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Upcoming Holidays */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Upcoming Holidays"
            subtitle="Public holidays coming up"
            actions={[
              <Button key="all" size="small" onClick={() => navigate("/Holiday")}>
                View all
              </Button>,
            ]}
          >
            {holidays.length === 0 ? (
              <EmptyState title="No upcoming holidays" compact />
            ) : (
              <Stack spacing={1.25}>
                {holidays.map((h, idx) => {
                  const hDate = new Date(h.date);
                  const daysLeft = Math.ceil((hDate - new Date()) / 86400000);
                  return (
                    <Stack
                      key={h._id || idx}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ p: 1.25, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
                    >
                      <Box
                        sx={{
                          minWidth: 48, textAlign: "center", p: 0.75, borderRadius: 1.5,
                          bgcolor: "#EDE8FA", color: "#6C50BF",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
                          {hDate.toLocaleDateString("en-IN", { month: "short" }).toUpperCase()}
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>
                          {hDate.getDate()}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{h.name || h.holidayName}</Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          {hDate.toLocaleDateString("en-IN", { weekday: "long" })}
                          {daysLeft === 0 ? " · Today" : daysLeft === 1 ? " · Tomorrow" : ` · in ${daysLeft} days`}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </Grid>

        {/* Celebrations */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Celebrations"
            subtitle="Birthdays & anniversaries this month"
          >
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}>
              <Tab label={<Stack direction="row" spacing={0.75} alignItems="center"><CakeOutlinedIcon sx={{ fontSize: 16 }} />Birthdays ({celebrations.birthdays?.length || 0})</Stack>} />
              <Tab label={<Stack direction="row" spacing={0.75} alignItems="center"><CelebrationOutlinedIcon sx={{ fontSize: 16 }} />Anniversaries ({celebrations.anniversaries?.length || 0})</Stack>} />
            </Tabs>
            {(() => {
              const list = tab === 0 ? celebrations.birthdays : celebrations.anniversaries;
              if (!list?.length) {
                return <EmptyState title={tab === 0 ? "No birthdays in next 30 days" : "No anniversaries in next 30 days"} compact />;
              }
              const sorted = [...list].sort((a, b) => {
                const aToday = isSameCalendarDay(a.occursOn) ? 0 : 1;
                const bToday = isSameCalendarDay(b.occursOn) ? 0 : 1;
                if (aToday !== bToday) return aToday - bToday;
                return new Date(a.occursOn) - new Date(b.occursOn);
              });
              return (
                <Stack spacing={1.25} sx={{ maxHeight: 240, overflowY: "auto" }}>
                  {sorted.slice(0, 8).map((p) => {
                    const todayPerson = isSameCalendarDay(p.occursOn);
                    return (
                      <Stack
                        key={p.employeeid}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                          p: todayPerson ? 1 : 0,
                          mx: todayPerson ? -1 : 0,
                          borderRadius: 2,
                          bgcolor: todayPerson
                            ? tab === 0
                              ? isDark
                                ? "rgba(236,72,153,0.12)"
                                : "#FDF2F8"
                              : isDark
                                ? "rgba(16,185,129,0.12)"
                                : "#ECFDF5"
                            : "transparent",
                          border: todayPerson ? "1px solid" : "none",
                          borderColor: todayPerson
                            ? tab === 0
                              ? "rgba(236,72,153,0.3)"
                              : "rgba(16,185,129,0.3)"
                            : "transparent",
                        }}
                      >
                        <Avatar
                          src={p.profilePicture}
                          sx={{
                            bgcolor: tab === 0 ? "#F4EDFA" : "#EDFAF2",
                            color: tab === 0 ? "#8a3fb5" : "#1a7f27",
                            width: 36,
                            height: 36,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {(p.name || "U").slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            {new Date(p.occursOn).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                            {tab === 1 && p.years && ` · ${p.years} year${p.years > 1 ? "s" : ""}`}
                            {todayPerson ? " · Today" : ""}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={todayPerson ? "Today" : tab === 0 ? "Birthday" : "Anniversary"}
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: todayPerson ? 700 : 500,
                            bgcolor: todayPerson
                              ? tab === 0
                                ? "#FCE7F3"
                                : "#D1FAE5"
                              : "action.hover",
                            color: todayPerson ? (tab === 0 ? "#BE185D" : "#047857") : "text.secondary",
                            border: "none",
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              );
            })()}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
